import { useState, useEffect } from 'react';
import { fsCollectionListener } from '@monash/portal-frontend-common';
import c from './feedback-manager.module.scss';
import FeedbackItem from './FeedbackItem';
import {
  SearchBox,
  Tag,
  FilterDropdown,
  Card,
  Button,
} from '@monash/portal-react';
import {
  FEEDBACK_APPS_FILTERS,
  OPT_IN_FILTERS,
  PRIORITY_FILTERS,
  PRIORITY_TAGS,
  STATUS_FILTERS,
  SORT_OPTIONS,
  PRIORITY_LEVEL,
} from './constants';
import IndividualFeedback from './individual-feedback/IndividualFeedback';

const FeedbackManager = () => {
  const [feedbackItems, setFeedbackItems] = useState([]);
  const [searchTarget, setSearchTarget] = useState('');
  const [selectedApp, setSelectedApp] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [selectedOptIn, setSelectedOptIn] = useState([]);
  const [selectedPriority, setSelectedPriority] = useState([]);
  const [selectedSort, setSelectedSort] = useState('newest');
  const [selectedFeedback, setSelectedFeedback] = useState('');

  const searching = searchTarget.length > 0;

  const hasFiltersApplied =
    selectedApp.length > 0 ||
    selectedStatus.length > 0 ||
    selectedOptIn.length > 0 ||
    selectedPriority.length > 0;

  const matchesSelectedAppFilter = (feedbackItem, selectedApp) => {
    if (!selectedApp.length) return false;
    return selectedApp.some((selectedValue) => {
      const [app, area] = selectedValue.split('_');
      if (app === area) {
        return feedbackItem.app === app;
      }
      return feedbackItem.app === app && feedbackItem.area === area;
    });
  };

  const lowerSearchTarget = searchTarget.toLowerCase();

  const searchedFeedback = searching
    ? feedbackItems.filter((item) => {
        return (
          item.replicateIssue?.toLowerCase().includes(lowerSearchTarget) ||
          item.jiraLinks?.some((link) =>
            link?.toLowerCase().includes(lowerSearchTarget)
          ) ||
          item.adminNotes?.some((note) =>
            note?.message.toLowerCase().includes(lowerSearchTarget)
          )
        );
      })
    : feedbackItems;

  const filteredFeedback = hasFiltersApplied
    ? searchedFeedback.filter(
        (feedbackItem) =>
          matchesSelectedAppFilter(feedbackItem, selectedApp) ||
          selectedStatus.includes(feedbackItem.status) ||
          selectedOptIn.includes(feedbackItem.optIn) ||
          selectedPriority.includes(feedbackItem.priority)
      )
    : searchedFeedback;

  const priorityFilterOptions = PRIORITY_FILTERS.map((value) => ({
    value,
    text: (
      <Tag
        text={PRIORITY_TAGS[value].label}
        color={PRIORITY_TAGS[value].colour}
      />
    ),
  }));

  const sortedFeedback = [...filteredFeedback].sort((a, b) => {
    switch (selectedSort) {
      case 'oldest':
        return a.createdAt - b.createdAt;

      case 'priority_lowest':
        return PRIORITY_LEVEL[a.priority] - PRIORITY_LEVEL[b.priority];

      case 'priority_highest':
        return PRIORITY_LEVEL[b.priority] - PRIORITY_LEVEL[a.priority];

      case 'last_updated':
        return b.lastEdit - a.lastEdit;

      case 'newest':
      default:
        return b.createdAt - a.createdAt;
    }
  });

  useEffect(() => {
    const pushSurveys = (snapshot) => {
      const tempArray = [];
      snapshot.forEach((doc) => {
        const documentData = { ...doc.data(), id: doc.id };
        tempArray.push(documentData);
      });
      tempArray.sort((a, b) => a.createdAt - b.createdAt);
      let counter = 1;
      for (const document in tempArray) {
        tempArray[document].displayID = counter;
        counter++;
      }
      setFeedbackItems(tempArray);
    };

    const onError = (error) => {
      console.error('Error fetching survey data:', error);
    };

    const getCollection = fsCollectionListener(
      'surveyFeedback',
      pushSurveys,
      onError
    );

    return () => getCollection();
  }, []);

  if (selectedFeedback) {
    const chosenFeedback = sortedFeedback.find((item) => {
      return item?.id === selectedFeedback;
    });
    return (
      <IndividualFeedback
        feedbackItem={chosenFeedback}
        setSelectedFeedback={setSelectedFeedback}
        selectedFeedbackId={selectedFeedback}
      />
    );
  }
  return (
    <div className={c.container}>
      <div className={c.header}>
        <h1 tabIndex={-1}>Feedback Manager</h1>
        <p>Click on a feedback item to view details and manage feedback</p>
      </div>
      <div className={c.body}>
        <div className={c.searchBar}>
          <SearchBox
            searchString={searchTarget}
            onInput={setSearchTarget}
            placeholder="Search by feedback text, admin note or Jira link"
          />
          <div className={c.select} />
        </div>
        <div className={c.filterBar}>
          <div className={c.filters}>
            <FilterDropdown
              placeholder="App name and area"
              filters={FEEDBACK_APPS_FILTERS}
              selectedFilter={selectedApp}
              setSelectedFilter={setSelectedApp}
              multi={true}
              align="left"
            />
            <FilterDropdown
              title="Status"
              label="Status"
              filters={STATUS_FILTERS}
              selectedFilter={selectedStatus}
              setSelectedFilter={setSelectedStatus}
              multi={true}
              align="left"
            />
            <FilterDropdown
              title="Opt-in"
              label="Opt-in"
              multi={true}
              filters={OPT_IN_FILTERS}
              selectedFilter={selectedOptIn}
              setSelectedFilter={setSelectedOptIn}
              align="left"
            />
            <FilterDropdown
              title="Priority"
              label="Priority"
              multi={true}
              filters={priorityFilterOptions}
              selectedFilter={selectedPriority}
              setSelectedFilter={setSelectedPriority}
              align="left"
            />
            {hasFiltersApplied && (
              <Button
                size="small"
                variant="text"
                onClick={() => {
                  setSelectedApp([]);
                  setSelectedStatus([]);
                  setSelectedOptIn([]);
                  setSelectedPriority([]);
                }}
              >
                Clear filters
              </Button>
            )}
          </div>
          <div className={c.filters}>
            <p>Sort by:</p>
            <FilterDropdown
              title="Date Recorded"
              label="Sort By"
              selectedFilter={selectedSort}
              setSelectedFilter={setSelectedSort}
              filters={SORT_OPTIONS}
              align="right"
            />
          </div>
        </div>
        <div>
          <Card>
            <table className={c.feedbackTable}>
              <thead>
                <tr>
                  <th className={c.id}>ID</th>
                  <th className={c.appName}>App name</th>
                  <th className={c.appArea}>App area</th>
                  <th>Date recorded</th>
                  <th>Status</th>
                  <th>Reason</th>
                  <th>Priority</th>
                  <th>Last updated</th>
                  <th className={c.actionMenu} />
                </tr>
              </thead>
              <tbody>
                {sortedFeedback.map((item, index) => (
                  <FeedbackItem
                    key={item.id}
                    item={item}
                    index={index}
                    setSelectedID={setSelectedFeedback}
                  />
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default FeedbackManager;
